import React, { useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { useSAINUsageCriteriaMet } from '../../common/controller/analytics/hooks/use-sain-usage';
import { CardTemplate } from '../../constants';
import { useAIAnswerContext } from '../ai-answer-context';
import { NLPSearchResultFormat } from '../types';

import { AIAnswerBodyWrapper } from './ai-answer-body-wrapper';
import { AIAnswerErrorWrapper } from './ai-answer-error-wrapper';
import { AIAnswerError } from './ai-answer-result/ai-answer-error';
import { JsonResultComponent } from './ai-answer-result/json/json-result-component';
import { PlaintextResultComponent } from './ai-answer-result/plaintext/plaintext-result-component';
import { RenderedResultComponent } from './ai-answer-result/rendered/rendered-result-component';

const AIAnswerResult = () => {
	const { commonAttributes } = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { answerFormat } = commonAttributes;

	useEffect(() => {
		fireSAINUsageEvent({ commonAttributes });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const AIAnswerResultComponent = useMemo(() => {
		switch (answerFormat) {
			case NLPSearchResultFormat.ADF:
			case NLPSearchResultFormat.MARKDOWN:
				return RenderedResultComponent;
			case NLPSearchResultFormat.JSON:
				return JsonResultComponent;
			case NLPSearchResultFormat.PLAINTEXT:
			default:
				return PlaintextResultComponent;
		}
	}, [answerFormat]);
	return <AIAnswerResultComponent />;
};

export const AIAnswerBody = (): React.JSX.Element => {
	const { isAnswered, hasError, isReadingAids, cardTemplate } = useAIAnswerContext();

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	return (
		<AIAnswerBodyWrapper
			isReadingAids={isReadingAids && cardTemplate === CardTemplate.DEFAULT}
			serveDefinitionsUXExp={serveDefinitionUXUpdates}
		>
			{isAnswered && !hasError ? (
				<AIAnswerResult />
			) : hasError ? (
				<AIAnswerErrorWrapper>
					<AIAnswerError />
				</AIAnswerErrorWrapper>
			) : null}
		</AIAnswerBodyWrapper>
	);
};
